import React, { useEffect, useState } from 'react';
import { Search, ChevronDown, Heart, ExternalLink, MoreHorizontal, Bookmark, X, Share2, Star, Diamond, Filter, BookmarkPlus } from 'lucide-react';
import { BookmarkGenerationFlow } from './BookmarkGenerationFlow';
interface BookmarkItem {
  id: string;
  type: 'angel' | 'skin' | 'post' | 'video';
  title: string;
  creator: {
    name: string;
    avatar: string;
    verified?: boolean;
  };
  thumbnail: string;
  timestamp: string;
  isFavorite: boolean;
}
export const BookmarksSection = () => {
  const [activeTab, setActiveTab] = useState<'all' | 'angels' | 'skins' | 'posts' | 'videos'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilterOptions, setShowFilterOptions] = useState(false);
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'popular'>('newest');
  const [bookmarks, setBookmarks] = useState<BookmarkItem[]>([]);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedBookmark, setSelectedBookmark] = useState<string | null>(null);
  const [streakDays, setStreakDays] = useState(3); // Mock streak data
  const [showBookmarkFlow, setShowBookmarkFlow] = useState(false);
  const [ritualId, setRitualId] = useState<string | undefined>(undefined);
  const [ritualTarget, setRitualTarget] = useState<number>(1);
  // Mock data for bookmarks
  useEffect(() => {
    // Simulate loading bookmarks from an API
    const mockBookmarks: BookmarkItem[] = [{
      id: '1',
      type: 'angel',
      title: 'Harper',
      creator: {
        name: 'Harper',
        avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8g53dZ7J3kRR9dJNnGuDbc/h2.png',
        verified: true
      },
      thumbnail: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8g53dZ7J3kRR9dJNnGuDbc/h2.png',
      timestamp: '2 days ago',
      isFavorite: true
    }, {
      id: '2',
      type: 'post',
      title: 'Just finished my workout...',
      creator: {
        name: 'Bella Blue',
        avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/5Qzoa8joo8CQwNu9hB7M4f/b3.png',
        verified: true
      },
      thumbnail: 'https://uploadthingy.s3.us-west-1.amazonaws.com/5Qzoa8joo8CQwNu9hB7M4f/b3.png',
      timestamp: '5 hours ago',
      isFavorite: false
    }, {
      id: '3',
      type: 'video',
      title: 'Private video message',
      creator: {
        name: 'Mia Khalifa',
        avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gPbfjPdLfzNbqrZitYWXeS/MiaKhalifa.jpg',
        verified: true
      },
      thumbnail: 'https://uploadthingy.s3.us-west-1.amazonaws.com/c9u3gUPe4JsXpyUu5nHEjr/mia_khalifa_1.png',
      timestamp: '1 week ago',
      isFavorite: true
    }, {
      id: '4',
      type: 'skin',
      title: 'Naughty Nurse Outfit',
      creator: {
        name: 'Zoey',
        avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/fnQWPJWMus62iBnJCYdU4W/z1.png'
      },
      thumbnail: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9yVK4ggPFZKtDWQ3fdeJ3s/z3.png',
      timestamp: '3 days ago',
      isFavorite: false
    }, {
      id: '5',
      type: 'post',
      title: 'Feeling festive in my special outfit...',
      creator: {
        name: 'Mia Khalifa',
        avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gPbfjPdLfzNbqrZitYWXeS/MiaKhalifa.jpg',
        verified: true
      },
      thumbnail: 'https://uploadthingy.s3.us-west-1.amazonaws.com/wa8aw3EX126n2GWo67428z/mia_khalifa_2.png',
      timestamp: '2 days ago',
      isFavorite: false
    }, {
      id: '6',
      type: 'angel',
      title: 'Lana',
      creator: {
        name: 'Lana',
        avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/cLp9w3hC4dVahxHuCeYw1i/488653244_669275086051024_406085801635574977_n.jpg',
        verified: true
      },
      thumbnail: 'https://uploadthingy.s3.us-west-1.amazonaws.com/cLp9w3hC4dVahxHuCeYw1i/488653244_669275086051024_406085801635574977_n.jpg',
      timestamp: '1 day ago',
      isFavorite: true
    }];
    setBookmarks(mockBookmarks);
  }, []);
  // Listen for bookmark ritual events
  useEffect(() => {
    const handleBookmarkRitual = (event: any) => {
      if (event.detail && event.detail.ritualId) {
        setRitualId(event.detail.ritualId);
        setRitualTarget(event.detail.target || 1);
        setShowBookmarkFlow(true);
      }
    };
    document.addEventListener('bookmarkRitual', handleBookmarkRitual as EventListener);
    return () => {
      document.removeEventListener('bookmarkRitual', handleBookmarkRitual as EventListener);
    };
  }, []);
  // Listen for ritual progress events
  useEffect(() => {
    const handleRitualProgress = (event: any) => {
      if (event.detail && event.detail.ritualId) {
        // Dispatch event to update ritual progress in RitualContext
        const updateEvent = new CustomEvent('updateRitualProgress', {
          detail: {
            ritualId: event.detail.ritualId,
            progress: event.detail.progress
          }
        });
        document.dispatchEvent(updateEvent);
      }
    };
    document.addEventListener('ritualProgress', handleRitualProgress as EventListener);
    return () => {
      document.removeEventListener('ritualProgress', handleRitualProgress as EventListener);
    };
  }, []);
  // Filter bookmarks based on active tab and search query
  const filteredBookmarks = bookmarks.filter(bookmark => {
    // Filter by tab
    if (activeTab !== 'all' && bookmark.type !== activeTab) {
      return false;
    }
    // Filter by search query
    if (searchQuery && !bookmark.title.toLowerCase().includes(searchQuery.toLowerCase()) && !bookmark.creator.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });
  // Sort bookmarks based on sortBy
  const sortedBookmarks = [...filteredBookmarks].sort((a, b) => {
    if (sortBy === 'newest') {
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    } else if (sortBy === 'oldest') {
      return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
    } else {
      // 'popular' - just a mock implementation
      return b.isFavorite === a.isFavorite ? 0 : b.isFavorite ? 1 : -1;
    }
  });
  const toggleFavorite = (id: string) => {
    setBookmarks(prev => prev.map(bookmark => bookmark.id === id ? {
      ...bookmark,
      isFavorite: !bookmark.isFavorite
    } : bookmark));
  };
  const removeBookmark = (id: string) => {
    setBookmarks(prev => prev.filter(bookmark => bookmark.id !== id));
    setShowRemoveModal(false);
  };
  const handleMoreOptions = (id: string) => {
    setSelectedBookmark(id);
    setShowRemoveModal(true);
  };
  const handleNavigateToContent = (bookmark: BookmarkItem) => {
    // In a real app, this would navigate to the content
    console.log(`Navigating to ${bookmark.type}: ${bookmark.title}`);
  };
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'angel':
        return 'bg-blue-500 dark:bg-blue-600';
      case 'skin':
        return 'bg-purple-500 dark:bg-purple-600';
      case 'post':
        return 'bg-pink-500 dark:bg-pink-600';
      case 'video':
        return 'bg-red-500 dark:bg-red-600';
      default:
        return 'bg-gray-500 dark:bg-gray-600';
    }
  };
  const handleAddBookmark = (angelId: string, bookmarkData: BookmarkItem) => {
    // Add the new bookmark to the list
    setBookmarks(prev => [bookmarkData, ...prev]);
  };
  return <div className="w-full max-w-7xl mx-auto" data-id="element-2455">
      <div className="mb-6" data-id="element-2456">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1" data-id="element-2457">
          My Bookmarks
        </h1>
        <p className="text-gray-600 dark:text-gray-400" data-id="element-2458">
          Easily revisit your saved Angels, posts, and content.
        </p>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-3 mb-6" data-id="element-2459">
        <div className="relative flex-1" data-id="element-2460">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2461">
            <Search size={18} className="text-gray-400" data-id="element-2462" />
          </div>
          <input type="text" placeholder="Search bookmarks..." className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 dark:focus:ring-pink-600" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-2463" />
        </div>
        <div className="flex gap-2" data-id="element-2464">
          <div className="relative" data-id="element-2465">
            <button className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => setShowFilterOptions(!showFilterOptions)} data-id="element-2466">
              <Filter size={18} className="text-gray-500 dark:text-gray-400" data-id="element-2467" />
              <span className="text-gray-700 dark:text-gray-300" data-id="element-2468">Filter</span>
              <ChevronDown size={16} className="text-gray-500 dark:text-gray-400" data-id="element-2469" />
            </button>
            {showFilterOptions && <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10" data-id="element-2470">
                <div className="p-2" data-id="element-2471">
                  <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" data-id="element-2472">
                    Sort by
                  </div>
                  <button className={`w-full text-left px-3 py-2 text-sm rounded-md ${sortBy === 'newest' ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`} onClick={() => {
                setSortBy('newest');
                setShowFilterOptions(false);
              }} data-id="element-2473">
                    Newest first
                  </button>
                  <button className={`w-full text-left px-3 py-2 text-sm rounded-md ${sortBy === 'oldest' ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`} onClick={() => {
                setSortBy('oldest');
                setShowFilterOptions(false);
              }} data-id="element-2474">
                    Oldest first
                  </button>
                  <button className={`w-full text-left px-3 py-2 text-sm rounded-md ${sortBy === 'popular' ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`} onClick={() => {
                setSortBy('popular');
                setShowFilterOptions(false);
              }} data-id="element-2475">
                    Most popular
                  </button>
                </div>
              </div>}
          </div>
          <button className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg hover:shadow-md hover:shadow-pink-500/20 transition-all" onClick={() => setShowBookmarkFlow(true)} data-id="element-2476">
            <BookmarkPlus size={18} data-id="element-2477" />
            <span data-id="element-2478">Add Bookmark</span>
          </button>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex overflow-x-auto scrollbar-hide mb-6 border-b border-gray-200 dark:border-gray-800 pb-1" data-id="element-2479">
        <button className={`px-4 py-2 whitespace-nowrap font-medium text-sm transition-colors ${activeTab === 'all' ? 'text-pink-600 dark:text-pink-400 border-b-2 border-pink-600 dark:border-pink-400' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`} onClick={() => setActiveTab('all')} data-id="element-2480">
          All
        </button>
        <button className={`px-4 py-2 whitespace-nowrap font-medium text-sm transition-colors ${activeTab === 'angels' ? 'text-pink-600 dark:text-pink-400 border-b-2 border-pink-600 dark:border-pink-400' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`} onClick={() => setActiveTab('angels')} data-id="element-2481">
          Angels
        </button>
        <button className={`px-4 py-2 whitespace-nowrap font-medium text-sm transition-colors ${activeTab === 'skins' ? 'text-pink-600 dark:text-pink-400 border-b-2 border-pink-600 dark:border-pink-400' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`} onClick={() => setActiveTab('skins')} data-id="element-2482">
          Skins
        </button>
        <button className={`px-4 py-2 whitespace-nowrap font-medium text-sm transition-colors ${activeTab === 'posts' ? 'text-pink-600 dark:text-pink-400 border-b-2 border-pink-600 dark:border-pink-400' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`} onClick={() => setActiveTab('posts')} data-id="element-2483">
          Posts
        </button>
        <button className={`px-4 py-2 whitespace-nowrap font-medium text-sm transition-colors ${activeTab === 'videos' ? 'text-pink-600 dark:text-pink-400 border-b-2 border-pink-600 dark:border-pink-400' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`} onClick={() => setActiveTab('videos')} data-id="element-2484">
          Videos
        </button>
      </div>

      {/* Gamification Layer */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-2485">
        <div className="bg-gradient-to-r from-pink-900/20 to-purple-900/20 dark:from-pink-900/30 dark:to-purple-900/30 rounded-lg border border-pink-200/30 dark:border-pink-800/30 p-4" data-id="element-2486">
          <div className="flex items-center gap-2 mb-2" data-id="element-2487">
            <div className="text-pink-500 dark:text-pink-400" size={20} data-id="element-2488" />
            <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-2489">
              Streak Bookmarks
            </h3>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-3" data-id="element-2490">
            Bookmark daily for 7 days to earn +10 LuvTokens
          </p>
          <div className="flex items-center justify-between mb-1" data-id="element-2491">
            <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-2492">
              Progress
            </span>
            <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-2493">
              {streakDays}/7 days
            </span>
          </div>
          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-2494">
            <div className="h-full bg-gradient-to-r from-pink-500 to-purple-500 rounded-full" style={{
            width: `${streakDays / 7 * 100}%`
          }} data-id="element-2495"></div>
          </div>
        </div>
        <div className="bg-gradient-to-r from-blue-900/20 to-indigo-900/20 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-lg border border-blue-200/30 dark:border-blue-800/30 p-4" data-id="element-2496">
          <div className="flex items-center gap-2 mb-2" data-id="element-2497">
            <Star className="text-yellow-500 dark:text-yellow-400" size={20} data-id="element-2498" />
            <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-2499">
              Top Picks
            </h3>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-3" data-id="element-2500">
            Most-bookmarked Angels across SinShift
          </p>
          <div className="flex -space-x-2" data-id="element-2501">
            <div className="w-8 h-8 rounded-full border-2 border-white dark:border-gray-900 overflow-hidden" data-id="element-2502">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/8g53dZ7J3kRR9dJNnGuDbc/h2.png" alt="Harper" className="w-full h-full object-cover" data-id="element-2503" />
            </div>
            <div className="w-8 h-8 rounded-full border-2 border-white dark:border-gray-900 overflow-hidden" data-id="element-2504">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/5Qzoa8joo8CQwNu9hB7M4f/b3.png" alt="Bella" className="w-full h-full object-cover" data-id="element-2505" />
            </div>
            <div className="w-8 h-8 rounded-full border-2 border-white dark:border-gray-900 overflow-hidden" data-id="element-2506">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/cLp9w3hC4dVahxHuCeYw1i/488653244_669275086051024_406085801635574977_n.jpg" alt="Lana" className="w-full h-full object-cover" data-id="element-2507" />
            </div>
            <div className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-white dark:border-gray-900 bg-gray-100 dark:bg-gray-800 text-xs font-medium" data-id="element-2508">
              +5
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 dark:from-purple-900/30 dark:to-blue-900/30 rounded-lg border border-purple-200/30 dark:border-purple-800/30 p-4" data-id="element-2509">
          <div className="flex items-center gap-2 mb-2" data-id="element-2510">
            <Diamond className="text-blue-500 dark:text-blue-400" size={20} data-id="element-2511" />
            <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-2512">
              Exclusive Reminder
            </h3>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-3" data-id="element-2513">
            Premium users can auto-sync bookmarks into Favorites feed
          </p>
          <button className="w-full py-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg text-white text-sm font-medium" data-id="element-2514">
            Enable Auto-Sync
          </button>
        </div>
      </div>

      {/* Bookmarks Content */}
      {sortedBookmarks.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2515">
          {sortedBookmarks.map(bookmark => <div key={bookmark.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md dark:hover:shadow-pink-900/10 transition-shadow group" data-id="element-2516">
              <div className="relative aspect-video bg-gray-100 dark:bg-gray-900 cursor-pointer" onClick={() => handleNavigateToContent(bookmark)} data-id="element-2517">
                <img src={bookmark.thumbnail} alt={bookmark.title} className="w-full h-full object-cover" data-id="element-2518" />
                <div className="absolute top-2 left-2" data-id="element-2519">
                  <span className={`text-xs px-2 py-1 rounded-full text-white ${getTypeColor(bookmark.type)}`} data-id="element-2520">
                    {bookmark.type.charAt(0).toUpperCase() + bookmark.type.slice(1)}
                  </span>
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100" data-id="element-2521">
                  <button className="p-2 bg-white/20 backdrop-blur-sm rounded-full" data-id="element-2522">
                    <ExternalLink size={20} className="text-white" data-id="element-2523" />
                  </button>
                </div>
                <div className="absolute top-2 right-2 flex gap-1" data-id="element-2524">
                  <button className={`p-1.5 rounded-full ${bookmark.isFavorite ? 'bg-pink-500 text-white' : 'bg-black/30 backdrop-blur-sm text-white hover:bg-pink-500/70'}`} onClick={e => {
              e.stopPropagation();
              toggleFavorite(bookmark.id);
            }} data-id="element-2525">
                    <Heart size={16} fill={bookmark.isFavorite ? 'currentColor' : 'none'} data-id="element-2526" />
                  </button>
                  <button className="p-1.5 rounded-full bg-black/30 backdrop-blur-sm text-white hover:bg-gray-800/70" onClick={e => {
              e.stopPropagation();
              handleMoreOptions(bookmark.id);
            }} data-id="element-2527">
                    <MoreHorizontal size={16} data-id="element-2528" />
                  </button>
                </div>
              </div>
              <div className="p-3" data-id="element-2529">
                <h3 className="font-medium text-gray-900 dark:text-white mb-2 cursor-pointer hover:text-pink-600 dark:hover:text-pink-400 line-clamp-1" onClick={() => handleNavigateToContent(bookmark)} data-id="element-2530">
                  {bookmark.title}
                </h3>
                <div className="flex items-center justify-between" data-id="element-2531">
                  <div className="flex items-center gap-2" data-id="element-2532">
                    <div className="w-6 h-6 rounded-full overflow-hidden" data-id="element-2533">
                      <img src={bookmark.creator.avatar} alt={bookmark.creator.name} className="w-full h-full object-cover" data-id="element-2534" />
                    </div>
                    <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-2535">
                      {bookmark.creator.name}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2536">
                    {bookmark.timestamp}
                  </span>
                </div>
              </div>
            </div>)}
        </div> : <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center" data-id="element-2537">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700" data-id="element-2538">
            <Bookmark size={32} className="text-gray-400 dark:text-gray-500" data-id="element-2539" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2" data-id="element-2540">
            No saved temptations yet
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto" data-id="element-2541">
            Browse Angels & tap the 🔖 icon to save what excites you.
          </p>
          <button className="px-4 py-2 bg-gradient-to-r from-pink-600 to-purple-600 rounded-lg text-white font-medium" onClick={() => setShowBookmarkFlow(true)} data-id="element-2542">
            Add Your First Bookmark
          </button>
        </div>}

      {/* Remove Bookmark Modal */}
      {showRemoveModal && <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-id="element-2543">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-sm w-full animate-in zoom-in-95 duration-150" data-id="element-2544">
            <div className="p-4 border-b border-gray-200 dark:border-gray-800" data-id="element-2545">
              <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-2546">
                Bookmark Options
              </h3>
            </div>
            <div className="p-4 space-y-3" data-id="element-2547">
              <button className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-gray-800 dark:text-gray-200" onClick={() => {
            if (selectedBookmark) toggleFavorite(selectedBookmark);
            setShowRemoveModal(false);
          }} data-id="element-2548">
                <Heart size={18} className="text-pink-500" data-id="element-2549" />
                <span data-id="element-2550">Add to Favorites</span>
              </button>
              <button className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-gray-800 dark:text-gray-200" onClick={() => setShowRemoveModal(false)} data-id="element-2551">
                <Share2 size={18} className="text-blue-500" data-id="element-2552" />
                <span data-id="element-2553">Share</span>
              </button>
              <button className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-red-600" onClick={() => {
            if (selectedBookmark) removeBookmark(selectedBookmark);
          }} data-id="element-2554">
                <X size={18} data-id="element-2555" />
                <span data-id="element-2556">Remove Bookmark</span>
              </button>
            </div>
            <div className="p-3 border-t border-gray-200 dark:border-gray-800 flex justify-end" data-id="element-2557">
              <button className="px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" onClick={() => setShowRemoveModal(false)} data-id="element-2558">
                Cancel
              </button>
            </div>
          </div>
        </div>}

      {/* Bookmark Generation Flow */}
      <BookmarkGenerationFlow isOpen={showBookmarkFlow} onClose={() => setShowBookmarkFlow(false)} onBookmarkAdded={handleAddBookmark} ritualId={ritualId} ritualTarget={ritualTarget} data-id="element-2559" />

      <style jsx data-id="element-2560">{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>;
};